/* eslint-disable react/no-children-prop */
import React from "react";
import { useTranslation } from "react-i18next";
// form
import { z } from "zod";
import { useAppForm } from "@components/form/app-form";
// UI
import { Center, Alert } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";

const schema = z.object({
  username: z.string().email(),
  password: z.string(),
});

export type LoginFormValues = z.infer<typeof schema>;

interface LoginFormProps {
  onSubmit: (values: LoginFormValues) => Promise<void>;
  isError: boolean;
}

export const LoginForm = React.memo<LoginFormProps>(({ onSubmit, isError }) => {
  const { t } = useTranslation();

  const form = useAppForm({
    defaultValues: { username: "", password: "" },
    onSubmit: async ({ value }) => {
      await onSubmit(value);
    },
    validators: { onBlur: schema },
  });

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        await form.handleSubmit();
      }}
    >
      {isError ? (
        <Alert
          variant="light"
          color="red"
          title={t("loginPage.loginFailed.header")}
          icon={<IconInfoCircle />}
        >
          {t("loginPage.loginFailed.message")}
        </Alert>
      ) : null}

      <form.AppField
        name="username"
        children={(field) => {
          return (
            <field.TextField
              label={t("loginPage.usernameField.label")}
              errors={field.state.meta.errors}
            />
          );
        }}
      />

      <form.AppField
        name="password"
        children={(field) => {
          return (
            <field.Password
              label={t("loginPage.passwordField.label")}
              errors={field.state.meta.errors}
            />
          );
        }}
      />

      <Center mt="xl">
        <form.Subscribe
          selector={(state) => [state.canSubmit, state.isSubmitting]}
          children={([canSubmit, isSubmitting]) => (
            <form.SubmitButton loading={isSubmitting} disabled={!canSubmit}>
              {t("loginPage.submitButton.label")}
            </form.SubmitButton>
          )}
        />
      </Center>
    </form>
  );
});

LoginForm.displayName = "LoginForm";
